import { Divider, Skeleton } from "antd"
import { ArrowDown, ArrowUp, FileText, InfoIcon, Loader } from "lucide-react"
import { memo, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useLocation,
  useParams,
  useRevalidator,
  useSearchParams
} from "react-router"

import { getKnowledgeOSSessionHistory } from "~/api/knowledge-os"
import AgentQuoteSelection from "~/components/agent-quote-selection"
import RichTextEditor from "~/components/base-editor/RichTextEditor"
import URLS from "~/constants/urls"
import { EVENTS } from "~/containers/os-agents/constants"
import { replayV2HistoryIntoStore } from "~/containers/os-agents/v2-history-replay"
import { useGetAgentTools, useGetKosTools } from "~/hooks/os.agents"
import useParentData from "~/hooks/useParentData"
import useGlobalStore from "~/store/global"
import useOsAgentsIdStore from "~/store/os.agents_.$id"
import { ITokenInfo } from "~/types/global"
import { track } from "~/utils/amplitude"
import { getBaseEventProps } from "~/utils/amplitude-events"
import { getFileTypeAndIcon, removeDuplicates } from "~/utils/helpers"
import { withLinkedTenant } from "~/utils/linked-tenant"
import {
  parseQuotedMessage,
  stripInternalAccountId
} from "~/utils/parse-quoted-message"
import {
  SuggestionItem,
  getSuggestionLabel,
  getSuggestionPrompt
} from "~/types/knowledge-os"

import { ToolName } from "./agent-id-messages"
import { ToolName as ToolNameV2 } from "./agent-id-messages-v2"
import AgentSelectAccount from "./agent-select-account"

const MAX_HISTORY_RETRY_COUNT = 5
const MAX_EMPTY_HISTORY_RETRY_COUNT = 60 // 60 * 3s = ~3 minutes
const EMPTY_HISTORY_RETRY_INTERVAL = 3000

const AgentSessionHistory = ({
  sendMessageRef,
  hasNewMessage
}: {
  sendMessageRef: any
  hasNewMessage: boolean
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const revalidator = useRevalidator()
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const setMessages = useOsAgentsIdStore((store) => store.setMessages)
  const retryCountRef = useRef(0)
  const timerId = useRef<NodeJS.Timeout | null>()
  const { data: agentToolsData } = useGetAgentTools()
  const { data: kosToolsData } = useGetKosTools()
  const agentToolsList = agentToolsData?.data ?? []
  const kosToolsList = kosToolsData?.data ?? []
  const toolsList = [...agentToolsList, ...kosToolsList]
  const { id: existingSessionId = "" } = useParams()
  // The chat layout is mounted by os.agents_.$id AND by the new
  // rfp-os_.proactive-proposal.$id route (which reuses the same loader).
  // useParentData looks up loader data by pathname — read it from the
  // live location so we hit whichever route is actually active. Without
  // this, the proactive-proposal route resolves to null → history is
  // empty → the 3-minute empty-poll loop spins forever.
  const { pathname } = useLocation()
  const historyResponse = useParentData<{
    id: string
    history: any[]
    enableAgentV2?: boolean
  }>(pathname, "", "")
  const ActiveToolName = historyResponse?.enableAgentV2 ? ToolNameV2 : ToolName
  const [polledHistory, setPolledHistory] = useState<any[] | null>(null)
  const history = useMemo(() => {
    if (polledHistory && polledHistory.length > 0) return polledHistory
    return historyResponse?.id === existingSessionId
      ? historyResponse?.history
      : []
  }, [historyResponse, existingSessionId, polledHistory])
  const lastMessage = history[history.length - 1]
  const emptyRetryCountRef = useRef(0)
  const emptyTimerId = useRef<NodeJS.Timeout | null>()

  // Retry when history is empty (e.g. page refresh before backend persists
  // data, or another tab is mid-stream and rows haven't landed yet).
  // Branches on the active flow:
  //   - v2 sessions: poll /v2/.../history and replay into useOsAgentsIdStore
  //     via replayV2HistoryIntoStore (same path the route loader uses).
  //   - v1 sessions: legacy path — poll /v1/.../history, hydrate the local
  //     `polledHistory` state.
  // Either way, polling stops once the first non-empty payload arrives or
  // MAX_EMPTY_HISTORY_RETRY_COUNT is hit.
  useEffect(() => {
    if (
      existingSessionId &&
      history.length === 0 &&
      !hasNewMessage &&
      tokenInfo
    ) {
      emptyRetryCountRef.current = 0
      emptyTimerId.current = setInterval(async () => {
        if (emptyRetryCountRef.current >= MAX_EMPTY_HISTORY_RETRY_COUNT) {
          clearInterval(emptyTimerId.current!)
          emptyTimerId.current = null
          return
        }
        emptyRetryCountRef.current += 1
        try {
          if (historyResponse?.enableAgentV2) {
            const res = await fetch(
              withLinkedTenant(
                `${URLS.V1.replace("/v1", "/v2")}/tenant/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/copilot/chatbot/session/${existingSessionId}/history`
              ),
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${tokenInfo.token}`,
                  "Content-Type": "application/json"
                }
              }
            )
            const json = await res.json()
            const items = json?.items || []
            if (items.length > 0) {
              const reattachHint = replayV2HistoryIntoStore(
                existingSessionId,
                items,
                setMessages
              )
              clearInterval(emptyTimerId.current!)
              emptyTimerId.current = null
              // Race: when the route loader's history fetch came back
              // empty (page reload arrived before api-getorbitx
              // persisted the row), the route's mount-effect skipped
              // replay → the route's reattach trigger didn't fire.
              // This poll caught the row later, so WE need to fire
              // reattach. Mirrors the route's own retry shape.
              if (reattachHint) {
                const tryReattach = (attempt: number) => {
                  const fn = (sendMessageRef?.current as any)?.reattach
                  if (typeof fn === "function") {
                    fn(
                      existingSessionId,
                      reattachHint.assistantMessageId,
                      reattachHint.invocationId
                    )
                    return
                  }
                  if (attempt < 12) {
                    setTimeout(() => tryReattach(attempt + 1), 50)
                  } else {
                    // eslint-disable-next-line no-console
                    console.warn(
                      `[v2-reattach] (poll) sendMessageRef.reattach unavailable after ${attempt} retries — ` +
                        `invocation=${reattachHint.invocationId} will stay pending until next user action`
                    )
                  }
                }
                tryReattach(0)
              }
            }
          } else {
            const res = await getKnowledgeOSSessionHistory(
              tokenInfo as ITokenInfo,
              existingSessionId
            )
            const data = res?.data?.data?.history || []
            if (data.length > 0) {
              setPolledHistory(data)
              clearInterval(emptyTimerId.current!)
              emptyTimerId.current = null
            }
          }
        } catch {
          // ignore, will retry
        }
      }, EMPTY_HISTORY_RETRY_INTERVAL)
      return () => {
        if (emptyTimerId.current) {
          clearInterval(emptyTimerId.current)
          emptyTimerId.current = null
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    existingSessionId,
    history.length,
    hasNewMessage,
    tokenInfo,
    historyResponse?.enableAgentV2,
    setMessages
  ])

  // Reset polled history when session changes
  useEffect(() => {
    setPolledHistory(null)
  }, [existingSessionId])

  useEffect(() => {
    const lastMessageStatus = lastMessage?.status
    if (lastMessageStatus === "IN_PROGRESS") {
      timerId.current = setInterval(() => {
        if (retryCountRef.current >= MAX_HISTORY_RETRY_COUNT) {
          clearInterval(timerId.current!)
          timerId.current = null
          return
        }
        retryCountRef.current += 1
        revalidator.revalidate()
      }, 10000)
      return () => {
        if (timerId.current) {
          clearInterval(timerId.current)
          timerId.current = null
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage])

  useEffect(() => {
    const existingAcocuntId = searchParams.get("account")
    if (!existingAcocuntId) {
      const lastTwoMessages = history.slice(-2)
      const accountId = lastTwoMessages.find(
        (item: any) => item.actor === "USER"
      )?.account_id
      if (accountId) {
        searchParams.set("account", accountId)
      } else {
        searchParams.delete("account")
      }
      setSearchParams(searchParams)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history])

  useEffect(() => {
    return () => {
      if (timerId.current) {
        clearInterval(timerId.current)
        timerId.current = null
      }
    }
  }, [existingSessionId])

  if (!history?.length) {
    // Suppress the empty-history poll spinner once the live store has
    // any messages for this session — for proactive-proposal / PPT
    // flows the route loader's history fetch returns empty for a while
    // (api-getorbitx persists the turn row only after chat-side done),
    // so the 3-minute empty poll keeps re-running and renders a wheel
    // even after the user message + assistant steps + deck are already
    // on screen.
    if (emptyTimerId.current && !hasNewMessage) {
      return (
        <div className="flex-1 flex items-center justify-center py-8">
          <Loader
            className="animate-spin text-primary dark:text-dark-6"
            size={32}
          />
        </div>
      )
    }
    return null
  }

  // If the loader's history ends with an IN_PROGRESS assistant placeholder
  // (the gray skeleton) AND the live store has its own assistant entry
  // streaming for the same turn (`hasNewMessage`), the placeholder is a
  // duplicate that the store version is filling in real-time. Trim it from
  // the render so we don't show "skeleton above + full content below" for
  // the same reply.
  const trailingInProgress =
    history.length > 0 &&
    history[history.length - 1]?.actor === "ASSISTANT" &&
    history[history.length - 1]?.status === "IN_PROGRESS"
  const renderedHistory =
    trailingInProgress && hasNewMessage ? history.slice(0, -1) : history

  return (
    <div className="flex flex-col gap-4">
      {renderedHistory.map((item: any, index: number) => (
        <SessionMessage
          key={item.timestamp}
          item={item}
          toolsList={toolsList}
          sendMessageRef={sendMessageRef}
          isLast={index === renderedHistory.length - 1}
          hasNewMessage={hasNewMessage}
          // @ts-expect-error sendMessageRef prop signature mismatch
          ToolNameComponent={ActiveToolName}
        />
      ))}
    </div>
  )
}

const SessionMessage = ({
  item,
  toolsList,
  sendMessageRef,
  isLast,
  hasNewMessage,
  ToolNameComponent
}: {
  item: any
  toolsList: any
  sendMessageRef: any
  isLast: boolean
  hasNewMessage: boolean
  ToolNameComponent: typeof ToolName
}) => {
  const { t } = useTranslation()
  const setSourcesDrawerData = useOsAgentsIdStore(
    (store) => store.setSourcesDrawerData
  )
  const setQuotedText = useOsAgentsIdStore((store) => store.setQuotedText)
  const pendingMessage = useOsAgentsIdStore((store) => store.pendingMessage)
  const isUser = item.actor === "USER"
  const isError = item.status === "DOWNSTREAM_FAILURE"
  const isInProgress = item.status === "IN_PROGRESS"
  const isUiComponentMessage = item.content_type === "ui_component"
  const isAccountSelectionTool = item.content_type === "account_selection"

  const parsedUserMessage = isUser
    ? item.context_quote
      ? { contextQuote: item.context_quote, userMessage: item.text }
      : parseQuotedMessage(item.text)
    : null
  const userQuoteExcerpt = parsedUserMessage?.contextQuote ?? null
  const displayText = isUser
    ? stripInternalAccountId(
        userQuoteExcerpt ? parsedUserMessage!.userMessage : item.text
      )
    : item.text

  // Skip rendering a history row that's just an in-progress assistant
  // placeholder with no content yet — the live store is filling it in via
  // <AgentIdMessages>, so painting the empty avatar bubble here only
  // produces a phantom row above the real streaming answer.
  if (
    !isUser &&
    isInProgress &&
    !displayText &&
    !item.files?.length &&
    !isUiComponentMessage &&
    !isAccountSelectionTool &&
    hasNewMessage
  ) {
    return null
  }

  return (
    <>
      <div
        className={`flex flex-col gap-2 relative ${isUser ? "w-fit max-w-[70%] ml-auto" : "max-w-[80%]"}`}
      >
        {item.files?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {item.files.map((file: File) => {
              const fileType = getFileTypeAndIcon(file.name)
              return (
                <div
                  key={file.name}
                  className="flex items-start gap-2 bg-white dark:bg-dark-2 border border-light-border dark:border-border px-2 py-[6px] rounded-lg"
                >
                  <div className={`p-2 rounded-md ${fileType.color}`}>
                    <FileText size={16} className="text-dark-6" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-dark-2 dark:text-dark-6 text-ellipsis max-w-24 overflow-hidden whitespace-nowrap">
                      {file.name}
                    </span>
                    <span className="text-xs text-dark-2 dark:text-dark-6">
                      {fileType.type}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        {isUser && userQuoteExcerpt && (
          <div className="flex items-start gap-1.5 text-13 text-dark-2/60 dark:text-dark-6/60 border-l-2 border-primary/40 pl-2 self-stretch">
            <span className="text-primary/60 flex-shrink-0">↳</span>
            <p className="line-clamp-2 italic m-0 text-left flex-1">
              {userQuoteExcerpt}
            </p>
          </div>
        )}
        <div className="flex items-start gap-4">
          {!isUser && (
            <div className="border border-primary dark:bg-dark-3 p-1 rounded-full flex-shrink-0">
              <img src="/favicon.png" alt="" className="w-5 h-5" />
            </div>
          )}
          {/* Only paint the antd skeleton band for a genuine "nothing yet"
              state. If the item already has streamed text OR the live store
              has a new-message entry filling in the response, the skeleton
              renders alongside the real content and looks like a duplicate
              (the intermittent "gray bars above the rendered answer" bug). */}
          {isInProgress && !displayText && !hasNewMessage && (
            <Skeleton active />
          )}
          {displayText &&
            (!isUser ? (
              <AgentQuoteSelection
                onQuote={setQuotedText}
                disabled={isInProgress}
              >
                <RichTextEditor
                  hideToolBar
                  className="p-0 p-13 text-dark-2 dark:text-dark-6 flex-1 justify-start"
                  content={displayText}
                  contentClassName="transparent-body"
                />
              </AgentQuoteSelection>
            ) : (
              <RichTextEditor
                hideToolBar
                className="p-0 p-13 text-dark-2 dark:text-dark-6 flex justify-end px-4 py-[10px] bg-genesis dark:bg-dark-2 rounded-[10px]"
                content={displayText}
                contentClassName="transparent-body"
              />
            ))}
          {isError && (
            <div className="border border-red-400 dark:border-red-300 rounded-xl px-3 py-[6px] flex items-center gap-2 w-fit text-xs text-red-400 dark:text-red-300">
              <InfoIcon size={16} />
              {"Something went wrong"}
            </div>
          )}
        </div>
        {isAccountSelectionTool && (
          <div className="max-w-2xl pl-11">
            <AgentSelectAccount
              label={t("os_agents.placeholder_select_an_account")}
            />
          </div>
        )}
        {isUiComponentMessage && (
          <ToolNameComponent
            toolsList={toolsList}
            resourceName={item.resourceName}
            content={item}
            sendMessageRef={sendMessageRef}
          />
        )}
        {(() => {
          const sources = item.llmVerifiedSources || item.source || []
          const uniqueSources = removeDuplicates(sources, "name")
          if (!uniqueSources || uniqueSources.length === 0) return null
          return (
            <div className="flex flex-row gap-2 py-4 ml-11 items-center">
              <button
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white dark:bg-dark-2 border border-light-border dark:border-border hover:bg-gray-50 dark:hover:bg-dark-1 transition-colors"
                onClick={() => {
                  track("source_clicked", {
                    source_name: "View all sources",
                    sources_available: uniqueSources.map(
                      (source: any) => source.name
                    ),
                    triggered_from: "os_agents_history"
                  })
                  setSourcesDrawerData(uniqueSources)
                }}
              >
                <span className="text-xs font-medium text-dark-2 dark:text-dark-6">
                  {t("label_sources")}
                </span>
                <ArrowDown className="w-3 h-3 text-dark-2 dark:text-dark-6 rotate-[-90deg]" />
              </button>
            </div>
          )
        })()}
        {item.score?.score_category && item.score?.score_category !== "NA" && (
          <p className="rounded-lg text-xs bg-genesis dark:bg-dark-1 text-primary dark:text-blue-300 border-none mt-1">
            Confidence:{" "}
            <span className="font-semibold">
              {item.score.score_category.replace(/Confidence/gi, "")}
            </span>
          </p>
        )}
      </div>
      {item.suggestions && item.suggestions.length > 0 && (
        <div className="flex flex-col gap-2 items-end">
          <h3 className="text-xs tracking-wide text-gray-600 dark:text-gray-400">
            {t("label_suggested_actions")}
          </h3>
          {item.suggestions.map((suggestion: SuggestionItem, index: number) => (
            <button
              key={index}
              className="w-fit text-left flex items-center gap-2 text-xs text-dark-2 dark:text-dark-6 border border-secondary bg-white dark:bg-dark-3 px-3 py-1 rounded-xl"
              onClick={() => {
                track(EVENTS.SUGGESTION_CLICKED, {
                  ...getBaseEventProps({ feature: "os_agents" }),
                  suggestion_label: getSuggestionLabel(suggestion)
                })
                sendMessageRef.current?.({
                  tool_id: "",
                  agentQuery: getSuggestionPrompt(suggestion),
                  displayText: getSuggestionLabel(suggestion)
                })
              }}
              disabled={!!pendingMessage}
            >
              {getSuggestionLabel(suggestion)}
              <div
                className={`bg-primary dark:text-primary-light flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-lg`}
              >
                <ArrowUp size={12} className="text-dark-6" />
              </div>
            </button>
          ))}
        </div>
      )}
      {!isUser &&
        item.suggestions &&
        item.suggestions.length > 0 &&
        (!isLast || hasNewMessage) && <Divider className="m-0" />}
    </>
  )
}

export default memo(AgentSessionHistory)
